import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaFilter, FaSlidersH } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import './filters.css';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useApp } from '../../context/AppContext';

const Filters = ({ filters, setFilters, isExpanded, setIsExpanded, setActiveFiltersCount }) => {
  const { t } = useApp();
  const [expandedCategories, setExpandedCategories] = useState({});
  const [closingCategories, setClosingCategories] = useState({});
  const [isClosingModal, setIsClosingModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const categoryDropdownRef = React.useRef(null);

  // Scroll animation
  const filtersRef = useScrollAnimation('fade-left');

  // Handle modal close with animation
  const handleCloseModal = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setIsExpanded(false);
      setIsClosingModal(false);
    }, 300); // Match the animation duration
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isExpanded]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        // Start closing animation for all expanded categories
        const expandedKeys = Object.keys(expandedCategories).filter(key => expandedCategories[key]);
        if (expandedKeys.length > 0) {
          const closingState = {};
          expandedKeys.forEach(key => {
            closingState[key] = true;
          });
          setClosingCategories(closingState);
          
          // After animation completes, actually close them
          setTimeout(() => {
            setExpandedCategories({});
            setClosingCategories({});
          }, 200); // Match animation duration
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedCategories]);

  // Fetch category counts from API
  useEffect(() => {
    // fetchCategoryCounts can be added later if needed
  }, []);

  // EuroAuctions-style hierarchical categories
  const mainCategories = [
    {
      id: 'utilaje-constructii',
      name: t.categoryNames['utilaje-constructii'],
      subcategories: [
        { id: 'excavatoare', name: t.categoryNames['excavatoare'], 
          items: ['excavatoare pe șenile', 'excavatoare pe roți', 'excavatoare pentru fier vechi', 'mini excavatoare'] 
        },
        { id: 'buldoexcavatoare', name: t.categoryNames['buldoexcavatoare'] },
        { id: 'buldozere', name: t.categoryNames['buldozere'] },
        { id: 'cilindri-compactori', name: t.categoryNames['cilindri-compactori'],
          items: ['Cilindri compactori', 'Cilindri mici', 'Cilindri tandem', 'Cilindri Polipneu']
        },
        { id: 'gredere', name: t.categoryNames['gredere'] },
        { id: 'incarcatoare', name: t.categoryNames['incarcatoare'],
          items: ['Încărcătoare frontale', 'Încărcătoare telescopice', 'Mini încărcătoare']
        },
        { id: 'dumpere', name: t.categoryNames['dumpere'] },
        { id: 'automacarale', name: t.categoryNames['automacarale'] },
        { id: 'autobetoniere', name: t.categoryNames['autobetoniere'] },
        { id: 'masini-asfalt', name: t.categoryNames['masini-asfalt'] },
        { id: 'sivuitoare', name: t.categoryNames['sivuitoare'] },
        { id: 'nacele', name: t.categoryNames['nacele'] },
        { id: 'statii-sortare', name: t.categoryNames['statii-sortare'] },
        { id: 'concasoare', name: t.categoryNames['concasoare'] },
        { id: 'accesorii-echipamente', name: t.categoryNames['accesorii-echipamente'] },
        { id: 'dezmembrari', name: t.categoryNames['dezmembrari'] },
        { id: 'diverse', name: t.categoryNames['diverse'] }
      ]
    },
    {
      id: 'transport',
      name: t.categoryNames['transport'],
      subcategories: [
        { id: 'camioane', name: t.categoryNames['camioane'] },
        { id: 'trailere', name: t.categoryNames['trailere'] },
        { id: 'semiremorci', name: t.categoryNames['semiremorci'] },
        { id: 'matura-stradala', name: t.categoryNames['matura-stradala'] }
      ]
    },
    {
      id: 'utilaje-agricole',
      name: t.categoryNames['utilaje-agricole'],
      subcategories: [
        { id: 'tractoare', name: t.categoryNames['tractoare'] },
        { id: 'combine', name: t.categoryNames['combine'] },
        { id: 'echipamente-agricole', name: t.categoryNames['echipamente-agricole'] }
      ]
    },
    {
      id: 'autoturisme-autoutilitare',
      name: t.categoryNames['autoturisme-autoutilitare'],
      subcategories: [
        { id: 'autoturisme', name: t.categoryNames['autoturisme'] },
        { id: 'autoutilitare', name: t.categoryNames['autoutilitare'] },
        { id: 'remorci', name: t.categoryNames['remorci'] }
      ]
    }
  ];

  const conditions = [
    { id: 'nou', name: t.conditionTypes['nou'] },
    { id: 'second-hand', name: t.conditionTypes['second-hand'] },
    { id: 'reconditionat', name: t.conditionTypes['reconditionat'] }
  ];

  const brands = [
    'Caterpillar', 'Volvo', 'Komatsu', 'JCB', 'Hitachi', 
    'Liebherr', 'Doosan', 'Hyundai', 'Bomag', 'Hamm'
  ];

  const handleCategoryChange = (categoryId) => {
    setFilters(prev => {
      // Toggle category in array instead of replacing
      const currentCategories = Array.isArray(prev.category) ? prev.category : 
                                (prev.category === 'all' ? [] : [prev.category]);
      
      if (currentCategories.includes(categoryId)) {
        // Remove if already selected
        const newCategories = currentCategories.filter(c => c !== categoryId);
        return { ...prev, category: newCategories.length > 0 ? newCategories : 'all' };
      } else {
        // Add to selection
        return { ...prev, category: [...currentCategories, categoryId] };
      }
    });
  };

  const toggleCategory = (categoryId) => {
    // If already expanded, start closing animation
    if (expandedCategories[categoryId]) {
      setClosingCategories(prev => ({
        ...prev,
        [categoryId]: true
      }));
      
      // After animation completes, actually close it
      setTimeout(() => {
        setExpandedCategories(prev => ({
          ...prev,
          [categoryId]: false
        }));
        setClosingCategories(prev => ({
          ...prev,
          [categoryId]: false
        }));
      }, 200); // Match animation duration
    } else {
      // Opening - just toggle it open
      setExpandedCategories(prev => ({
        ...prev,
        [categoryId]: true
      }));
    }
  };

  const handleConditionToggle = (conditionId) => {
    setFilters(prev => ({
      ...prev,
      condition: prev.condition.includes(conditionId)
        ? prev.condition.filter(id => id !== conditionId)
        : [...prev.condition, conditionId]
    }));
  };

  const handleBrandToggle = (brand) => {
    setFilters(prev => ({
      ...prev,
      brands: prev.brands.includes(brand)
        ? prev.brands.filter(b => b !== brand)
        : [...prev.brands, brand]
    }));
  };

  const handlePriceChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      priceRange: { ...prev.priceRange, [field]: value }
    }));
  };

  const handleYearChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      yearRange: { ...prev.yearRange, [field]: value }
    }));
  };

  const handleHoursChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      hoursRange: { ...prev.hoursRange, [field]: value }
    }));
  };

  const resetFilters = () => {
    setFilters({
      category: 'all',
      condition: [],
      priceRange: { min: '', max: '' },
      yearRange: { min: '', max: '' },
      hoursRange: { min: '', max: '' },
      brands: []
    });
    
    // Clear URL parameters
    navigate('/machines', { replace: true });
  };

  const removeFilter = (type, value) => {
    switch(type) {
      case 'category':
        setFilters(prev => {
          const currentCategories = Array.isArray(prev.category) ? prev.category : [prev.category];
          const newCategories = currentCategories.filter(c => c !== value);
          const categoryValue = newCategories.length > 0 ? newCategories : 'all';
          
          // Update URL
          const urlParams = new URLSearchParams(location.search);
          urlParams.delete('category');
          if (categoryValue !== 'all') {
            const categoriesArray = Array.isArray(categoryValue) ? categoryValue : [categoryValue];
            categoriesArray.forEach(cat => urlParams.append('category', cat));
          }
          const newSearch = urlParams.toString();
          navigate(newSearch ? `/machines?${newSearch}` : '/machines', { replace: true });
          
          return { ...prev, category: categoryValue };
        });
        break;
      case 'condition':
        setFilters(prev => ({ ...prev, condition: prev.condition.filter(id => id !== value) }));
        break;
      case 'brand':
        setFilters(prev => ({ ...prev, brands: prev.brands.filter(b => b !== value) }));
        break;
      case 'price':
        setFilters(prev => ({ ...prev, priceRange: { min: '', max: '' } }));
        break;
      case 'year':
        setFilters(prev => ({ ...prev, yearRange: { min: '', max: '' } }));
        break;
      case 'hours':
        setFilters(prev => ({ ...prev, hoursRange: { min: '', max: '' } }));
        break;
      default:
        break;
    }
  };

  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (filters.category !== 'all') {
      // Handle both array and single category
      const categories = Array.isArray(filters.category) ? filters.category : [filters.category];
      
      categories.forEach(catId => {
        let categoryName = catId.toUpperCase();
        
        mainCategories.forEach(mainCat => {
          mainCat.subcategories.forEach(subcat => {
            if (subcat.id === catId) {
              categoryName = subcat.name;
            }
            if (subcat.items) {
              subcat.items.forEach(item => {
                if (item.toLowerCase().replace(/\s+/g, '-') === catId) {
                  categoryName = item;
                }
              });
            }
          });
        });
        
        activeFilters.push({ type: 'category', label: categoryName, value: catId });
      });
    }
    
    filters.condition.forEach(condId => {
      const cond = conditions.find(c => c.id === condId);
      if (cond) {
        activeFilters.push({ type: 'condition', label: cond.name, value: condId });
      }
    });
    
    filters.brands.forEach(brand => {
      activeFilters.push({ type: 'brand', label: brand, value: brand });
    });
    
    if (filters.priceRange.min || filters.priceRange.max) {
      const label = `${t.filters.price}: $${filters.priceRange.min || '0'} - $${filters.priceRange.max || '∞'}`;
      activeFilters.push({ type: 'price', label, value: 'price' });
    }
    
    if (filters.yearRange.min || filters.yearRange.max) {
      const label = `${t.filters.year}: ${filters.yearRange.min || '0'} - ${filters.yearRange.max || t.filters.now}`;
      activeFilters.push({ type: 'year', label, value: 'year' });
    }
    
    if (filters.hoursRange.min || filters.hoursRange.max) {
      const label = `${t.filters.hours}: ${filters.hoursRange.min || '0'} - ${filters.hoursRange.max || '∞'}`;
      activeFilters.push({ type: 'hours', label, value: 'hours' });
    }
    
    return activeFilters;
  };

  const activeFilters = getActiveFilters();
  const activeFiltersCount = activeFilters.length;

  // Update parent component with active filters count
  useEffect(() => {
    if (setActiveFiltersCount) {
      setActiveFiltersCount(activeFiltersCount);
    }
  }, [activeFiltersCount, setActiveFiltersCount]);

  return (
    <>
    <section className="mainfilters-filters-section" ref={filtersRef}>
      <div className="mainfilters-filters-container">
        {/* EuroAuctions-style Hierarchical Categories */}
        <div className="mainfilters-category-section-euro" ref={categoryDropdownRef}>
          <div className="mainfilters-main-categories-grid">
            {mainCategories.map(mainCat => (
              <div 
                key={mainCat.id} 
                className={`mainfilters-main-category-card ${expandedCategories[mainCat.id] ? 'expanded' : ''}`}
              >
                <div 
                  className="mainfilters-main-category-header"
                  onClick={() => toggleCategory(mainCat.id)}
                >
                  <h3 className="mainfilters-main-category-title">{mainCat.name}</h3>
                  <span className={`mainfilters-expand-icon ${expandedCategories[mainCat.id] ? 'expanded' : ''}`}>
                    {expandedCategories[mainCat.id] ? '−' : '+'}
                  </span>
                </div>
                
                {expandedCategories[mainCat.id] && (
                  <div className={`mainfilters-subcategories-list ${closingCategories[mainCat.id] ? 'closing' : ''}`}>
                    {mainCat.subcategories.map((subcat, index) => {
                      const isActive = Array.isArray(filters.category) 
                        ? filters.category.includes(subcat.id)
                        : filters.category === subcat.id;
                      
                      return (
                        <div key={subcat.id} className="mainfilters-subcategory-item">
                          <button
                            className={`mainfilters-subcategory-btn ${isActive ? 'active' : ''}`}
                            onClick={() => handleCategoryChange(subcat.id)}
                          >
                            <span className="mainfilters-subcategory-number">{index + 1}.</span>
                            <span className="mainfilters-subcategory-name">{subcat.name}</span>
                            {isActive && <span className="mainfilters-check-icon">✓</span>}
                          </button>
                          
                          {subcat.items && subcat.items.length > 0 && (
                            <div className="mainfilters-subcategory-items">
                              {subcat.items.map(item => {
                                const itemId = item.toLowerCase().replace(/\s+/g, '-');
                                const isItemActive = Array.isArray(filters.category)
                                  ? filters.category.includes(itemId)
                                  : filters.category === itemId;
                                
                                return (
                                  <button
                                    key={itemId}
                                    className={`mainfilters-item-btn ${isItemActive ? 'active' : ''}`}
                                    onClick={() => handleCategoryChange(itemId)}
                                  >
                                    ○ {t.subsubCategories[item] || item}
                                    {isItemActive && <span className="mainfilters-check-icon-small"> ✓</span>}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Active Filters Display */}
        <div className="mainfilters-active-filters-section">
          <div className="mainfilters-active-filters-header">
            <span className="mainfilters-active-filters-title">
              <FaFilter /> {t.filters.activeFilters} ({activeFilters.length})
            </span>
            {activeFilters.length > 0 && (
              <button className="mainfilters-clear-all-btn" onClick={resetFilters}>
                <FaTimes /> {t.filters.clearAll}
              </button>
            )}
          </div>
          <div className="mainfilters-active-filters-list">
            {activeFilters.length > 0 ? (
              activeFilters.map((filter, index) => (
                <div key={`${filter.type}-${index}`} className="mainfilters-active-filter-tag">
                  <span className="mainfilters-filter-tag-label">{filter.label}</span>
                  <button 
                    className="mainfilters-remove-filter-btn"
                    onClick={() => removeFilter(filter.type, filter.value)}
                    aria-label={`${t.filters.remove} ${filter.label}`}
                  >
                    <FaTimes />
                  </button>
                </div>
              ))
            ) : (
              <span className="mainfilters-no-filters-placeholder">
                {t.filters.noFiltersApplied}
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
    
    {/* Advanced Filters Modal - Rendered as Portal */}
    {isExpanded && ReactDOM.createPortal(
      <>
        {/* Modal Overlay */}
        <div 
          className={`mainfilters-modal-overlay ${isClosingModal ? 'closing' : ''}`}
          onClick={handleCloseModal}
        ></div>
        
        {/* Modal Content */}
        <div className={`mainfilters-advanced-filters-modal ${isClosingModal ? 'closing' : ''}`}>
          <div className="mainfilters-modal-header">
            <h2 className="mainfilters-modal-title">
              <FaSlidersH className="mainfilters-modal-icon" />
              {t.filters.advancedFilters}
            </h2>
            <button 
              className="mainfilters-modal-close-btn"
              onClick={handleCloseModal}
              aria-label={t.filters.closeFilters}
            >
              <FaTimes />
            </button>
          </div>
          
          <div className="mainfilters-modal-body">
            <div className="mainfilters-filters-grid">
              {/* Condition Filter */}
              <div className="mainfilters-filter-group">
                <h3 className="mainfilters-filter-title">
                  <FaFilter className="mainfilters-title-icon" />
                  {t.filters.condition}
                </h3>
                <div className="mainfilters-filter-options-list">
                  {conditions.map(cond => (
                    <button
                      key={cond.id}
                      className={`mainfilters-filter-option-large ${filters.condition.includes(cond.id) ? 'active' : ''}`}
                      onClick={() => handleConditionToggle(cond.id)}
                    >
                      <span className="mainfilters-option-text">{cond.name}</span>
                      {filters.condition.includes(cond.id) && (
                        <span className="mainfilters-check-icon">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mainfilters-filter-group">
                <h3 className="mainfilters-filter-title">
                  <FaFilter className="mainfilters-title-icon" />
                  {t.filters.priceRange}
                </h3>
                <div className="mainfilters-range-slider-wrapper">
                  <div className="mainfilters-range-inputs-row">
                    <div className="mainfilters-input-group">
                      <label>Min (€)</label>
                      <input
                        type="number"
                        className="mainfilters-range-input-large"
                        placeholder="0"
                        value={filters.priceRange.min}
                        onChange={(e) => handlePriceChange('min', e.target.value)}
                      />
                    </div>
                    <div className="mainfilters-range-divider">—</div>
                    <div className="mainfilters-input-group">
                      <label>Max (€)</label>
                      <input
                        type="number"
                        className="mainfilters-range-input-large"
                        placeholder="500,000"
                        value={filters.priceRange.max}
                        onChange={(e) => handlePriceChange('max', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="mainfilters-range-slider">
                    <input
                      type="range"
                      min="0"
                      max="500000"
                      step="5000"
                      value={filters.priceRange.min || 0}
                      onChange={(e) => handlePriceChange('min', e.target.value)}
                      className="mainfilters-slider mainfilters-slider-min"
                    />
                    <input
                      type="range"
                      min="0"
                      max="500000"
                      step="5000"
                      value={filters.priceRange.max || 500000}
                      onChange={(e) => handlePriceChange('max', e.target.value)}
                      className="mainfilters-slider mainfilters-slider-max"
                    />
                  </div>
                </div>
              </div>

              {/* Year Range */}
              <div className="mainfilters-filter-group">
                <h3 className="mainfilters-filter-title">
                  <FaFilter className="mainfilters-title-icon" />
                  {t.filters.yearLabel}
                </h3>
                <div className="mainfilters-range-slider-wrapper">
                  <div className="mainfilters-range-inputs-row">
                    <div className="mainfilters-input-group">
                      <label>{t.filters.minYear}</label>
                      <input
                        type="number"
                        className="mainfilters-range-input-large"
                        placeholder="2000"
                        value={filters.yearRange.min}
                        onChange={(e) => handleYearChange('min', e.target.value)}
                      />
                    </div>
                    <div className="mainfilters-range-divider">—</div>
                    <div className="mainfilters-input-group">
                      <label>{t.filters.maxYear}</label>
                      <input
                        type="number"
                        className="mainfilters-range-input-large"
                        placeholder="2025"
                        value={filters.yearRange.max}
                        onChange={(e) => handleYearChange('max', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="mainfilters-range-slider">
                    <input
                      type="range"
                      min="2000"
                      max="2025"
                      step="1"
                      value={filters.yearRange.min || 2000}
                      onChange={(e) => handleYearChange('min', e.target.value)}
                      className="mainfilters-slider mainfilters-slider-min"
                    />
                    <input
                      type="range"
                      min="2000"
                      max="2025"
                      step="1"
                      value={filters.yearRange.max || 2025}
                      onChange={(e) => handleYearChange('max', e.target.value)}
                      className="mainfilters-slider mainfilters-slider-max"
                    />
                  </div>
                </div>
              </div>

              {/* Hours Range */}
              <div className="mainfilters-filter-group">
                <h3 className="mainfilters-filter-title">
                  <FaFilter className="mainfilters-title-icon" />
                  {t.filters.workingHours}
                </h3>
                <div className="mainfilters-range-slider-wrapper">
                  <div className="mainfilters-range-inputs-row">
                    <div className="mainfilters-input-group">
                      <label>{t.filters.minHours}</label>
                      <input
                        type="number"
                        className="mainfilters-range-input-large"
                        placeholder="0"
                        value={filters.hoursRange.min}
                        onChange={(e) => handleHoursChange('min', e.target.value)}
                      />
                    </div>
                    <div className="mainfilters-range-divider">—</div>
                    <div className="mainfilters-input-group">
                      <label>{t.filters.maxHours}</label>
                      <input
                        type="number"
                        className="mainfilters-range-input-large"
                        placeholder="25,000"
                        value={filters.hoursRange.max}
                        onChange={(e) => handleHoursChange('max', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="mainfilters-range-slider">
                    <input
                      type="range"
                      min="0"
                      max="25000"
                      step="500"
                      value={filters.hoursRange.min || 0}
                      onChange={(e) => handleHoursChange('min', e.target.value)}
                      className="mainfilters-slider mainfilters-slider-min"
                    />
                    <input
                      type="range"
                      min="0"
                      max="25000"
                      step="500"
                      value={filters.hoursRange.max || 25000}
                      onChange={(e) => handleHoursChange('max', e.target.value)}
                      className="mainfilters-slider mainfilters-slider-max"
                    />
                  </div>
                </div>
              </div>

              {/* Brands Filter */}
              <div className="mainfilters-filter-group mainfilters-brands-filter-full">
                <h3 className="mainfilters-filter-title">
                  <FaFilter className="mainfilters-title-icon" />
                  {t.filters.brands}
                </h3>
                <div className="mainfilters-brands-grid-large">
                  {brands.map(brand => (
                    <button
                      key={brand}
                      className={`mainfilters-brand-option-large ${filters.brands.includes(brand) ? 'active' : ''}`}
                      onClick={() => handleBrandToggle(brand)}
                    >
                      <span className="mainfilters-brand-name">{brand}</span>
                      {filters.brands.includes(brand) && (
                        <span className="mainfilters-check-icon">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mainfilters-filter-actions">
              <button className="mainfilters-reset-filters-btn" onClick={resetFilters}>
                <FaTimes />
                {t.filters.clearAllFilters} ({activeFiltersCount})
              </button>
            </div>
          </div>
        </div>
      </>,
      document.body
    )}
    </>
  );
};

export default Filters;